import { useState, useEffect, useCallback } from 'react';
import documentService from '../services/documentService';
import cadrageService from '../services/cadrageService';
import useCaseService from '../services/useCaseService';
import { domainService } from '../services/domainService';
import i18n from '../i18n/config';
import { apiErrorDetail } from '../lib/knowledgeSharing';
import { MinioItem } from '../types';
import type { DocumentRecord, DocumentVisibility } from '../services/documentService';

export type DocumentsContext =
  | { type: 'domain'; id: string }
  | { type: 'cadrage'; id: string }
  | { type: 'use-case'; id: string };

/** Listing entry; files backed by a DB record also carry their knowledge visibility (lot K). */
export interface DocumentItem extends MinioItem {
  visibility?: DocumentVisibility;
  ownerId?: string;
}

export interface UseDocumentsReturn {
  folderContent: DocumentItem[];
  selectedFolderPath: string;
  setSelectedFolderPath: (path: string | null) => void;
  isLoading: boolean;
  error: string | null;
  uploadFile: (file: File) => Promise<void>;
  uploadFiles: (
    files: File[],
    onProgress?: (completed: number, total: number) => void
  ) => Promise<{ successCount: number; errorCount: number }>;
  createFolder: (folderName: string) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  deleteFolder: (folderPath: string) => Promise<void>;
  getDownloadUrl: (documentId: string) => Promise<string>;
  /** Local update of a listed document's visibility (the caller does the API call). */
  setItemVisibility: (documentId: string, visibility: DocumentVisibility) => void;
  /** When true, create folder and delete folder are supported (cadrage only for now). */
  supportsFolderActions: boolean;
}

function normalizeFolderPath(path: string): string {
  let p = path;
  if (p !== '/' && !p.endsWith('/')) p += '/';
  if (!p.startsWith('/')) p = '/' + p;
  return p;
}

function toMinioPrefix(folderPath: string): string {
  return folderPath === '/' ? '' : folderPath.replace(/^\//, '');
}

function mergeFolderContent(
  minioData: { subfolders?: MinioItem[]; files?: MinioItem[] },
  dbDocs: DocumentRecord[],
  docIdKey: 'id' | string = 'id'
): DocumentItem[] {
  // Map: storage filename (last segment of minio_path) -> document, so we match MinIO listing to DB
  const docsByStorageName = new Map<string, DocumentRecord>();
  dbDocs.forEach((doc) => {
    const path = (doc as any).minio_path ?? (doc as any).minioPath ?? '';
    const storageName = path ? path.split('/').filter(Boolean).pop()! : (doc.name ?? (doc as any).name);
    if (storageName) docsByStorageName.set(storageName, doc);
  });

  const combined: DocumentItem[] = [];
  const usedDocIds = new Set<string>();

  (minioData.subfolders || []).forEach((folder) => combined.push(folder));

  (minioData.files || []).forEach((file) => {
    const primary = docsByStorageName.get(file.name);
    if (primary) usedDocIds.add(String((primary as any).id ?? primary.id));
    const docId = primary ? String((primary as any)[docIdKey] ?? (primary as any).id) : undefined;
    const minioPath = (primary as any)?.minioPath ?? (primary as any)?.minio_path;
    const mimeType = (primary as any)?.mime_type ?? (primary as any)?.mimeType;
    const displayName = primary ? (primary.name ?? (primary as any).name) : undefined;
    combined.push({
      name: displayName || file.name,
      type: 'file',
      documentId: docId,
      minioPath: minioPath || undefined,
      mimeType,
      visibility: primary?.visibility,
      ownerId: primary?.owner_id,
    });
  });

  // Add DB docs that have no matching MinIO file (e.g. orphan DB record)
  dbDocs.forEach((doc) => {
    const docIdStr = String((doc as any).id ?? doc.id);
    if (usedDocIds.has(docIdStr)) return;
    const path = (doc as any).minio_path ?? (doc as any).minioPath;
    const storageName = path ? path.split('/').filter(Boolean).pop() : null;
    const alreadyInList = (minioData.files || []).some((f) => f.name === storageName || f.name === (doc.name ?? (doc as any).name));
    if (alreadyInList) return;
    combined.push({
      name: doc.name ?? (doc as any).name ?? 'document',
      type: 'file',
      documentId: docIdStr,
      minioPath: path,
      mimeType: (doc as any).mime_type ?? (doc as any).mimeType,
      visibility: doc.visibility,
      ownerId: doc.owner_id,
    });
  });

  combined.sort((a, b) => {
    if (a.type === 'folder' && b.type !== 'folder') return -1;
    if (a.type !== 'folder' && b.type === 'folder') return 1;
    return a.name.localeCompare(b.name);
  });
  return combined;
}

/**
 * Unified hook for domain, cadrage, and use-case documents. Uses documentService for all
 * document operations. Folder create/delete are supported for cadrage (cadrageService) and
 * use-case (useCaseService); for domain they throw (backend does not support yet).
 */
export function useDocuments(context: DocumentsContext | undefined): UseDocumentsReturn {
  const id = context?.id;
  const isCadrage = context?.type === 'cadrage';
  const isDomain = context?.type === 'domain';
  const isUseCase = context?.type === 'use-case';

  const [currentFolderPath, setCurrentFolderPath] = useState<string>('/');
  const [folderContent, setFolderContent] = useState<DocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const setSelectedFolderPath = useCallback((path: string | null) => {
    setCurrentFolderPath(path ?? '/');
  }, []);

  const apiFolderPath = normalizeFolderPath(currentFolderPath);
  const minioPrefix = toMinioPrefix(apiFolderPath);

  const fetchFolderContent = useCallback(async () => {
    if (!id || (!isCadrage && !isDomain && !isUseCase)) {
      setFolderContent([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    setFolderContent([]);

    try {
      let minioData: { subfolders?: MinioItem[]; files?: MinioItem[] };
      let dbDocs: DocumentRecord[];

      if (isCadrage) {
        minioData = await documentService.listFoldersByCadrage(id, { prefix: minioPrefix });
        dbDocs = await documentService.listByCadrage(id, { folder_path: apiFolderPath });
      } else if (isUseCase) {
        minioData = await documentService.listFoldersByUseCase(id, { prefix: minioPrefix });
        dbDocs = await documentService.listByAudit(id, { folder_path: apiFolderPath });
      } else {
        minioData = await domainService.listDomainFolders(id, minioPrefix);
        dbDocs = await documentService.listByDomain(id, { folder_path: apiFolderPath });
      }

      const combined = mergeFolderContent(minioData, dbDocs);
      setFolderContent(combined);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || i18n.t('errors.documents.fetchFolderFailed');
      setError(errorMsg);
      setFolderContent([]);
    } finally {
      setIsLoading(false);
    }
  }, [id, isCadrage, isDomain, isUseCase, apiFolderPath, minioPrefix]);

  useEffect(() => {
    fetchFolderContent();
  }, [fetchFolderContent]);

  const uploadFiles = useCallback(
    async (files: File[], onProgress?: (completed: number, total: number) => void) => {
      if (!id) throw new Error(i18n.t('errors.documents.missingContextForUpload'));
      if (!files.length) return { successCount: 0, errorCount: 0 };

      let completed = 0;
      let successCount = 0;
      let errorCount = 0;

      setIsLoading(true);
      setError(null);
      try {
        const uploadTasks = files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('folder_path', apiFolderPath);

          try {
            if (isCadrage) await documentService.uploadToCadrage(id, formData);
            else if (isUseCase) await documentService.uploadToAudit(id, formData);
            else await documentService.uploadToDomain(id, formData);
            successCount += 1;
          } catch (err: unknown) {
            const errorMsg =
              apiErrorDetail(err)
              || (err instanceof Error ? err.message : null)
              || i18n.t('errors.documents.uploadFailed');
            setError(errorMsg);
            errorCount += 1;
          } finally {
            completed += 1;
            onProgress?.(completed, files.length);
          }
        });

        await Promise.all(uploadTasks);
        await fetchFolderContent();
        return { successCount, errorCount };
      } finally {
        setIsLoading(false);
      }
    },
    [id, isCadrage, isUseCase, apiFolderPath, fetchFolderContent]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const result = await uploadFiles([file]);
      if (result.errorCount > 0) {
        throw new Error(i18n.t('errors.documents.uploadFailed'));
      }
    },
    [uploadFiles]
  );

  const createFolder = useCallback(
    async (folderName: string) => {
      if ((!isCadrage && !isUseCase) || !id) {
        throw new Error(i18n.t('errors.documents.folderCreationUnsupported'));
      }
      if (!folderName?.trim().endsWith('/')) {
        throw new Error(i18n.t('errors.documents.folderNameMustEndWithSlash'));
      }
      const currentRelative = currentFolderPath === '/' ? '' : currentFolderPath.replace(/^\//, '');
      const fullPath = currentRelative + folderName.trim();
      setIsLoading(true);
      setError(null);
      try {
        if (isUseCase) await useCaseService.createFolder(id, fullPath);
        else await cadrageService.createFolder(id, fullPath);
        await fetchFolderContent();
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || err.message || i18n.t('errors.documents.createFolderFailed');
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [id, isCadrage, isUseCase, currentFolderPath, fetchFolderContent]
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await documentService.delete(documentId);
        await fetchFolderContent();
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || err.message || i18n.t('errors.documents.deleteDocumentFailed');
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFolderContent]
  );

  const deleteFolder = useCallback(
    async (folderPath: string) => {
      if ((!isCadrage && !isUseCase) || !id) {
        throw new Error(i18n.t('errors.documents.folderDeletionUnsupported'));
      }
      const currentRelative = currentFolderPath === '/' ? '' : currentFolderPath.replace(/^\//, '');
      const fullPath = currentRelative + folderPath.replace(/\/$/, '') + (folderPath.endsWith('/') ? '' : '');
      setIsLoading(true);
      setError(null);
      try {
        if (isUseCase) await useCaseService.deleteFolder(id, fullPath);
        else await cadrageService.deleteFolder(id, fullPath);
        await fetchFolderContent();
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || err.message || i18n.t('errors.documents.deleteFolderFailed');
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [id, isCadrage, isUseCase, currentFolderPath, fetchFolderContent]
  );

  const getDownloadUrl = useCallback(async (documentId: string): Promise<string> => {
    return documentService.getDownloadUrl(documentId);
  }, []);

  const setItemVisibility = useCallback((documentId: string, visibility: DocumentVisibility) => {
    setFolderContent((prev) =>
      prev.map((item) => (item.documentId === documentId ? { ...item, visibility } : item))
    );
  }, []);

  return {
    folderContent,
    selectedFolderPath: currentFolderPath,
    setSelectedFolderPath,
    isLoading,
    error,
    uploadFile,
    uploadFiles,
    createFolder,
    deleteDocument,
    deleteFolder,
    getDownloadUrl,
    setItemVisibility,
    supportsFolderActions: isCadrage || isUseCase,
  };
}
